import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI } from '../lib/api';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Sheet, SheetContent, SheetTitle } from './ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  FolderOpen,
  CheckSquare,
  Users,
  Bell,
  LogOut,
  Settings,
  Menu,
  ChevronLeft,
  ChevronRight,
  FileText,
  CalendarDays,
  Store,
  ArrowRightLeft,
  GanttChart,
  Receipt,
  ChevronsLeft,
  ChevronsRight,
  FileSignature,
  BarChart3,
  Sofa,
  Package,
  Sparkles,
  Link2,
  Camera,
  FileCheck,
  Target,
  Layers,
  Layers3,
  Upload,
  CalendarPlus,
  ShoppingBag,
  LifeBuoy,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_44f04ad6-a165-46a5-bc68-49188ad32b32/artifacts/hy9ywl5e_WhatsApp%20Image%202026-03-09%20at%2019.34.44.jpeg";

const NAV_SECTIONS = [
  {
    label: 'Overview',
    color: 'var(--sidebar-sage)',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'staff'] },
      { path: '/client-dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['client'] },
      { path: '/crm', label: 'CRM / Leads', icon: Target, roles: ['admin', 'staff'] },
    ],
  },
  {
    label: 'Projects',
    color: 'var(--sidebar-ocean)',
    items: [
      { path: '/projects', label: 'Projects', icon: FolderOpen, roles: ['admin', 'staff', 'client'] },
      { path: '/project-templates', label: 'Templates', icon: Layers3, roles: ['admin', 'staff'] },
      { path: '/tasks', label: 'Tasks', icon: CheckSquare, roles: ['admin', 'staff'] },
      { path: '/calendar', label: 'Calendar', icon: CalendarDays, roles: ['admin', 'staff'] },
      { path: '/calendar-sync', label: 'Calendar Sync', icon: CalendarPlus, roles: ['admin', 'staff', 'client'] },
      { path: '/gantt', label: 'Timeline', icon: GanttChart, roles: ['admin', 'staff'] },
    ],
  },
  {
    label: 'Finance',
    color: 'var(--sidebar-amber)',
    items: [
      { path: '/invoices', label: 'Invoices', icon: FileText, roles: ['admin', 'staff'] },
      { path: '/proposals', label: 'Proposals', icon: FileSignature, roles: ['admin', 'staff'] },
      { path: '/assemblies', label: 'Assemblies', icon: Layers, roles: ['admin', 'staff'] },
      { path: '/contracts', label: 'Contracts', icon: FileCheck, roles: ['admin', 'staff'] },
      { path: '/expenditure', label: 'Expenditure', icon: Receipt, roles: ['admin', 'staff'] },
      { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'staff'] },
    ],
  },
  {
    label: 'Procurement',
    color: 'var(--sidebar-terra)',
    items: [
      { path: '/ffe-library', label: 'FF&E Library', icon: Sofa, roles: ['admin', 'staff'] },
      { path: '/procurement', label: 'Pipeline', icon: Package, roles: ['admin', 'staff'] },
      { path: '/purchase-orders', label: 'Purchase Orders', icon: ShoppingBag, roles: ['admin', 'staff'] },
      { path: '/product-clipper', label: 'Product Clipper', icon: Link2, roles: ['admin', 'staff'] },
      { path: '/vendors', label: 'Vendor Directory', icon: Store, roles: ['admin', 'staff'] },
      { path: '/vendor-compare', label: 'Vendor Compare', icon: ArrowRightLeft, roles: ['admin', 'staff'] },
    ],
  },
  {
    label: 'Admin',
    color: 'var(--sidebar-slate)',
    items: [
      { path: '/ai-assistant', label: 'AI Assistant', icon: Sparkles, roles: ['admin', 'staff'] },
      { path: '/bulk-import', label: 'Bulk Import', icon: Upload, roles: ['admin', 'staff'] },
      { path: '/help', label: 'Help & Support', icon: LifeBuoy, roles: ['admin', 'staff', 'client'] },
      { path: '/users', label: 'Users', icon: Users, roles: ['admin'] },
    ],
  },
];

function SidebarNav({ collapsed, userRole, onNavigate }) {
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/dashboard' || path === '/client-dashboard') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <nav className="flex-1 py-2 overflow-y-auto">
      {NAV_SECTIONS.map((section) => {
        const visibleItems = section.items.filter(item => item.roles.includes(userRole));
        if (visibleItems.length === 0) return null;

        return (
          <div key={section.label} className={collapsed ? 'mb-4' : ''}>
            {!collapsed && (
              <div className="sidebar-section-label" data-testid={`nav-section-${section.label.toLowerCase()}`}>
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle" style={{ background: `hsl(${section.color})` }} />
                {section.label}
              </div>
            )}
            {collapsed && <div className="mx-auto my-3 w-6 border-t border-border/60" />}
            {visibleItems.map((item) => {
              const active = isActive(item.path);
              const iconColor = `hsl(${section.color})`;
              const linkContent = (
                <Link
                  to={item.path}
                  onClick={onNavigate}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`sidebar-nav-item ${active ? 'sidebar-nav-active' : 'sidebar-nav-inactive'} ${collapsed ? 'justify-center px-0 mx-3' : ''}`}
                >
                  <item.icon className="sidebar-nav-icon" style={{ color: iconColor }} />
                  {!collapsed && <span className="sidebar-nav-text">{item.label}</span>}
                </Link>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.path} delayDuration={0}>
                    <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return <React.Fragment key={item.path}>{linkContent}</React.Fragment>;
            })}
          </div>
        );
      })}
    </nav>
  );
}

export const Layout = ({ children }) => {
  const { user, logout, isClient } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', String(collapsed));
  }, [collapsed]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      if (!active || !user) return;
      try {
        const response = await notificationsAPI.list();
        if (active) {
          setNotifications(response.data);
          setUnreadCount(response.data.filter(n => !n.read).length);
        }
      } catch (err) {
        console.error('Notification poll failed:', err.message);
      }
    };
    poll();
    const interval = setInterval(poll, 60000);
    return () => { active = false; clearInterval(interval); };
  }, [user]);

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark notifications as read:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  const userRole = user?.role || 'client';

  const sidebarContent = (isMobile) => (
    <div className="flex flex-col h-full">
      {/* Logo Area */}
      <div className={`sidebar-logo-area ${collapsed && !isMobile ? 'sidebar-logo-collapsed' : 'sidebar-logo-expanded'}`}>
        <Link to={isClient ? "/client-dashboard" : "/dashboard"} data-testid="logo-link" className="block">
          {collapsed && !isMobile ? (
            <div className="sidebar-monogram" data-testid="sidebar-monogram">XI</div>
          ) : (
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="The Eleventh" className="sidebar-logo-img" />
              <div className="min-w-0">
                <p className="text-sm font-bold tracking-wide text-foreground leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>THE ELEVENTH</p>
                <p className="text-[10px] text-muted-foreground tracking-widest uppercase">Design Studio</p>
              </div>
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <SidebarNav
          collapsed={collapsed && !isMobile}
          userRole={userRole}
          onNavigate={isMobile ? () => setMobileOpen(false) : undefined}
        />
      </ScrollArea>

      {/* Bottom Area */}
      <div className="border-t border-border">
        {/* Notifications */}
        <div className={`px-3 py-2 ${collapsed && !isMobile ? 'flex justify-center' : ''}`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`relative ${collapsed && !isMobile ? 'h-10 w-10 p-0' : 'w-full justify-start gap-3 px-4 h-10'}`}
                data-testid="notifications-btn"
              >
                <Bell className="h-4 w-4 shrink-0" />
                {!collapsed || isMobile ? <span className="text-sm">Notifications</span> : null}
                {unreadCount > 0 && (
                  <span className={`notification-badge rounded-full ${collapsed && !isMobile ? '' : 'relative top-0 right-0 ml-auto'}`}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={collapsed ? "center" : "start"} side="right" className="w-80">
              <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground">Notifications</span>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-auto py-1" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
                    Mark all read
                  </Button>
                )}
              </div>
              <ScrollArea className="h-[300px]">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">No notifications</div>
                ) : (
                  notifications.slice(0, 10).map((notification) => (
                    <div
                      key={notification.notification_id}
                      className={`px-4 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors cursor-pointer ${!notification.read ? 'bg-muted/30' : ''}`}
                      onClick={async () => {
                        if (!notification.read) {
                          try {
                            await notificationsAPI.markRead(notification.notification_id);
                            setNotifications(prev => prev.map(n =>
                              n.notification_id === notification.notification_id ? { ...n, read: true } : n
                            ));
                            setUnreadCount(prev => Math.max(0, prev - 1));
                          } catch (err) { console.error('Mark read failed:', err.message); }
                        }
                        if (notification.link) {
                          try { navigate(notification.link); } catch (e) { /* invalid route */ }
                        }
                      }}
                    >
                      <p className="text-sm">{notification.message}</p>
                      <span className="text-xs text-muted-foreground mt-1 block">
                        {new Date(notification.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))
                )}
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* User Profile */}
        <div className={`px-3 pb-3 ${collapsed && !isMobile ? 'flex justify-center' : ''}`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`sidebar-user-btn ${collapsed && !isMobile ? 'sidebar-user-collapsed' : 'sidebar-user-expanded'}`}
                data-testid="user-menu-btn"
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="avatar-initials rounded-full !w-8 !h-8 text-xs shrink-0">
                    {getInitials(user?.name)}
                  </div>
                )}
                {(!collapsed || isMobile) && (
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{user?.role}</p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={collapsed ? "center" : "start"} side="right" className="w-56">
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                <Settings className="mr-2 h-4 w-4" />Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                <LogOut className="mr-2 h-4 w-4" />Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={0}>
      <div className="sidebar-layout" data-testid="app-layout">
        {/* Desktop Sidebar */}
        <aside
          className={`sidebar-desktop ${collapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
          data-testid="desktop-sidebar"
        >
          {sidebarContent(false)}
          {/* Collapse Toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="sidebar-toggle-btn"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronsRight className="h-3.5 w-3.5" /> : <ChevronsLeft className="h-3.5 w-3.5" />}
          </button>
        </aside>

        {/* Mobile Sheet */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="p-0 w-80">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            {sidebarContent(true)}
          </SheetContent>
        </Sheet>

        {/* Main Content Area */}
        <div className="sidebar-main">
          {/* Mobile Top Bar */}
          <header className="sidebar-mobile-header" data-testid="mobile-header">
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Link to={isClient ? "/client-dashboard" : "/dashboard"} className="flex items-center">
              <img src={LOGO_URL} alt="The Eleventh" className="h-10 w-auto object-contain" />
            </Link>
            <div className="flex items-center gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative h-9 w-9" data-testid="mobile-notifications-btn">
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && <span className="notification-badge rounded-full">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] sm:w-80">
                  <div className="flex items-center justify-between px-4 py-2 border-b">
                    <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground">Notifications</span>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" className="text-xs h-auto py-1" onClick={handleMarkAllRead}>Mark all read</Button>
                    )}
                  </div>
                  <ScrollArea className="h-[250px]">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">No notifications</div>
                    ) : (
                      notifications.slice(0, 10).map((n) => (
                        <div key={n.notification_id} className={`px-4 py-3 border-b border-border/50 hover:bg-muted/50 cursor-pointer ${!n.read ? 'bg-muted/30' : ''}`}
                          onClick={async () => {
                            if (!n.read) { try { await notificationsAPI.markRead(n.notification_id); setNotifications(prev => prev.map(x => x.notification_id === n.notification_id ? { ...x, read: true } : x)); setUnreadCount(prev => Math.max(0, prev - 1)); } catch (e) {} }
                            if (n.link) navigate(n.link);
                          }}>
                          <p className="text-sm">{n.message}</p>
                          <span className="text-xs text-muted-foreground mt-1 block">{new Date(n.created_at).toLocaleDateString()}</span>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-user-btn">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <div className="avatar-initials rounded-full !w-7 !h-7 text-xs">{getInitials(user?.name)}</div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-4 py-3 border-b"><p className="text-sm font-medium">{user?.name}</p><p className="text-xs text-muted-foreground">{user?.email}</p></div>
                  <DropdownMenuItem onClick={() => navigate('/settings')}><Settings className="mr-2 h-4 w-4" />Settings</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Page Content */}
          <main className="sidebar-content">
            <div className="animate-fade-in">
              {children}
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Layout;
